import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Plus, Edit2, Trash2, ExternalLink, ShoppingBag, Package, Eye, EyeOff, Lock } from "lucide-react";
import ImageUploader from "../ImageUploader";
import StripeConnectCard from "../StripeConnectCard";

const KIND_OPTIONS = [
  { id: "digital",  label: "Digital Download", desc: "PDFs, audio, video files, ebooks — anything they download" },
  { id: "physical", label: "Physical Product", desc: "Books, mugs, tees — you ship it" },
];

export default function MyStoreTab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | productObject

  const load = () => {
    setLoading(true);
    api.get("/me/products")
      .then(r => setItems(r.data.products || []))
      .catch(() => toast.error("Couldn't load your products"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const isPaid = user && (user.tier || "free").toLowerCase() !== "free";

  // Free tier — locked card
  if (user && !isPaid) {
    return (
      <div className="space-y-5" data-testid="my-store-locked">
        <div className="rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#2A2521] text-white p-8 md:p-10 shadow-2xl border-2 border-[#F39A1F]/40 max-w-3xl mx-auto">
          <div className="flex flex-col items-center text-center gap-5">
            <Lock className="w-14 h-14 text-[#F39A1F]"/>
            <div>
              <div className="text-[10px] tracking-wider-cap font-bold text-[#F39A1F]">★ ALL ACCESS MEMBERS ONLY</div>
              <h3 className="font-serif-display text-3xl md:text-4xl text-white uppercase mt-1 leading-tight text-balance">Build Your Own Store. Keep 100% Of Your Sales.</h3>
              <p className="text-white/85 mt-3 text-base max-w-xl mx-auto text-balance">All Access members can sell digital downloads + physical products on their own /u/your-name storefront. We take ZERO cut. Your $49/mo membership IS the platform fee.</p>
              <Link to="/pricing?plan=faithful_monthly" className="mt-5 inline-block bg-[#F39A1F] text-[#0F172A] px-6 py-3 rounded-md font-bold uppercase tracking-wider-cap text-xs hover:bg-white transition shadow-lg">★ Get All Access · $49/mo</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-store-tab">
      <div>
        <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">★ MY STORE</div>
        <h2 className="text-3xl uppercase text-red-wine">Your Personal Storefront.</h2>
        <p className="text-foreground/85 mt-2 max-w-2xl text-base">List the digital downloads + physical products you sell. We take <strong className="text-red-wine">0%</strong> — you keep every dollar. Visitors see them at <code className="bg-foreground/5 px-1.5 py-0.5 rounded text-xs">/u/{user?.username || "your-name"}/store</code></p>
      </div>

      {/* Stripe Connect status — guided onboarding for member to receive direct payouts */}
      <StripeConnectCard />

      {/* Action bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-foreground/70">{items.length} {items.length === 1 ? "product" : "products"} · <Link to={`/u/${user?.username}/store`} target="_blank" rel="noopener noreferrer" className="text-red-wine font-bold hover:underline inline-flex items-center gap-1">View public storefront <ExternalLink className="w-3 h-3"/></Link></div>
        <button onClick={() => setEditing("new")} data-testid="add-product-btn" className="bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-4 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs inline-flex items-center gap-2 shadow"><Plus className="w-4 h-4"/> Add Product</button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-foreground/60 text-center py-10">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 rounded-xl bg-white/60 border-2 border-dashed border-[#F39A1F]/40" data-testid="my-store-empty">
          <Package className="w-10 h-10 text-[#F39A1F] mx-auto mb-2"/>
          <div className="font-serif-display text-2xl text-red-wine/80 uppercase">Your market is empty.</div>
          <p className="text-sm text-foreground/70 mt-1 mb-4">Add your first product — an ebook, a printable, an audio apologetics piece, or a physical item.</p>
          <button onClick={() => setEditing("new")} className="bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-5 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs inline-flex items-center gap-2"><Plus className="w-4 h-4"/> Add Your First Product</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-store-grid">
          {items.map(p => <ProductCard key={p.id} product={p} onEdit={() => setEditing(p)} onChange={load}/>)}
        </div>
      )}

      {editing && <ProductEditor product={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function ProductCard({ product, onEdit, onChange }) {
  const togglePublish = async () => {
    const newStatus = product.status === "published" ? "draft" : "published";
    try {
      await api.patch(`/me/products/${product.id}`, { status: newStatus });
      toast.success(newStatus === "published" ? "Now published — visible on your market" : "Unpublished");
      onChange();
    } catch (e) { toast.error("Couldn't update status"); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${product.title}"?`)) return;
    try {
      await api.delete(`/me/products/${product.id}`);
      toast.success("Deleted");
      onChange();
    } catch (e) { toast.error("Couldn't delete"); }
  };
  const isPub = product.status === "published";
  return (
    <div className="bg-white rounded-xl border-2 border-foreground/10 hover:border-[#F39A1F] hover:shadow-xl transition flex flex-col overflow-hidden" data-testid={`my-product-${product.id}`}>
      <div className="aspect-[16/10] bg-foreground/5 relative overflow-hidden">
        {product.image_url ? <img src={product.image_url} alt="" className="w-full h-full object-cover" loading="lazy"/> : <div className="w-full h-full flex items-center justify-center"><Package className="w-12 h-12 text-foreground/20"/></div>}
        <div className={`absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider-cap px-2 py-0.5 rounded-full ${isPub ? "bg-[#22693C] text-white" : "bg-foreground/30 text-white"}`}>{isPub ? "★ PUBLISHED" : "DRAFT"}</div>
        <div className="absolute top-2 right-2 bg-[#F39A1F] text-[#0F172A] text-xs font-bold px-2.5 py-0.5 rounded-full">${product.price_usd.toFixed(2)}</div>
        <div className="absolute bottom-2 left-2 text-[9px] font-bold uppercase tracking-wider-cap px-2 py-0.5 rounded-full bg-onyx/70 text-white backdrop-blur-sm">{product.kind === "digital" ? "DIGITAL" : "PHYSICAL"}</div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-bold text-red-wine text-base leading-tight mb-1 line-clamp-2">{product.title}</div>
        <div className="text-xs text-foreground/65 line-clamp-2 mb-3">{product.description}</div>
        {product.purchase_link && <div className="text-[10px] tracking-wider-cap font-bold text-foreground/50 mb-2">★ EXTERNAL LINK SET</div>}
        <div className="mt-auto flex items-center gap-1.5 pt-2 border-t border-foreground/10">
          <button onClick={onEdit} data-testid={`edit-product-${product.id}`} className="flex-1 inline-flex items-center justify-center gap-1 text-xs uppercase tracking-wider-cap font-bold text-foreground hover:text-red-wine py-2 px-2 rounded-md hover:bg-foreground/5"><Edit2 className="w-3 h-3"/> Edit</button>
          <button onClick={togglePublish} data-testid={`toggle-publish-${product.id}`} className="flex-1 inline-flex items-center justify-center gap-1 text-xs uppercase tracking-wider-cap font-bold text-foreground hover:text-red-wine py-2 px-2 rounded-md hover:bg-foreground/5">{isPub ? <><EyeOff className="w-3 h-3"/> Hide</> : <><Eye className="w-3 h-3"/> Publish</>}</button>
          <button onClick={remove} data-testid={`delete-product-${product.id}`} className="inline-flex items-center justify-center gap-1 text-xs uppercase tracking-wider-cap font-bold text-foreground hover:text-red-wine py-2 px-2 rounded-md hover:bg-foreground/5"><Trash2 className="w-3 h-3"/></button>
        </div>
      </div>
    </div>
  );
}

function ProductEditor({ product, onClose, onSaved }) {
  const [form, setForm] = useState(product ? {
    title: product.title || "", description: product.description || "", price_usd: product.price_usd || 0,
    kind: product.kind || "digital", image_url: product.image_url || "", file_url: product.file_url || "",
    inventory: product.inventory ?? "", shipping_required: !!product.shipping_required,
    purchase_link: product.purchase_link || "", status: product.status || "draft",
  } : { title: "", description: "", price_usd: 0, kind: "digital", image_url: "", file_url: "", inventory: "", shipping_required: false, purchase_link: "", status: "draft" });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (form.price_usd < 0) { toast.error("Price can't be negative"); return; }
    setSaving(true);
    try {
      const payload = { ...form, price_usd: parseFloat(form.price_usd) || 0, inventory: form.inventory === "" ? null : parseInt(form.inventory) };
      if (product) await api.patch(`/me/products/${product.id}`, payload);
      else await api.post("/me/products", payload);
      toast.success(product ? "Saved" : "Product created");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-onyx/70 z-50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose} data-testid="product-editor-modal">
      <div className="bg-white rounded-t-2xl md:rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl border-t-4 border-[#F39A1F]" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-[#0F172A] to-[#2A2521] text-white px-5 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="text-[10px] tracking-wider-cap font-bold text-[#F39A1F]">★ {product ? "EDIT PRODUCT" : "NEW PRODUCT"}</div>
            <div className="font-serif-display text-2xl">{product ? form.title || "Untitled" : "Add Product"}</div>
          </div>
          <button onClick={onClose} data-testid="editor-close" className="text-white/80 hover:text-white p-1.5 hover:bg-white/10 rounded">✕</button>
        </div>
        <form onSubmit={save} className="p-5 space-y-4">
          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} data-testid="prod-title" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm" placeholder="e.g. 30-Day Apologetics Workbook" maxLength={200}/>
          </div>
          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Description</label>
            <textarea rows={4} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} data-testid="prod-description" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm" placeholder="What it is, who it's for, what they'll get."/>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Price (USD) *</label>
              <input type="number" step="0.01" min="0" value={form.price_usd} onChange={e => setForm(f => ({...f, price_usd: e.target.value}))} data-testid="prod-price" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm" placeholder="14.99"/>
            </div>
            <div>
              <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Type *</label>
              <select value={form.kind} onChange={e => setForm(f => ({...f, kind: e.target.value}))} data-testid="prod-kind" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm bg-white">
                {KIND_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              <div className="text-[10px] text-foreground/55 mt-0.5">{KIND_OPTIONS.find(o => o.id === form.kind)?.desc}</div>
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Cover Image</label>
            <ImageUploader value={form.image_url} onChange={url => setForm(f => ({...f, image_url: url}))} testid="prod-image"/>
          </div>
          {form.kind === "digital" && (
            <div>
              <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Digital File URL · <span className="text-foreground/55 normal-case font-normal">paste a Google Drive / Dropbox / S3 link</span></label>
              <input value={form.file_url} onChange={e => setForm(f => ({...f, file_url: e.target.value}))} data-testid="prod-file-url" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm font-mono" placeholder="https://drive.google.com/..."/>
            </div>
          )}
          {form.kind === "physical" && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Inventory · <span className="text-foreground/55 normal-case font-normal">leave blank for unlimited</span></label>
                <input type="number" min="0" value={form.inventory} onChange={e => setForm(f => ({...f, inventory: e.target.value}))} data-testid="prod-inventory" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm" placeholder="50"/>
              </div>
              <div className="flex items-center pt-7">
                <label className="flex items-center gap-2 text-sm font-bold text-foreground cursor-pointer">
                  <input type="checkbox" checked={form.shipping_required} onChange={e => setForm(f => ({...f, shipping_required: e.target.checked}))} data-testid="prod-shipping" className="w-4 h-4"/>
                  Requires shipping address
                </label>
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Payment Link · <span className="text-foreground/55 normal-case font-normal">PayPal / Cash App / Square / Stripe — until our Connect goes live</span></label>
            <input value={form.purchase_link} onChange={e => setForm(f => ({...f, purchase_link: e.target.value}))} data-testid="prod-purchase-link" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm font-mono" placeholder="https://paypal.me/yourname/14.99"/>
          </div>
          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Visibility</label>
            <div className="flex gap-2">
              {["draft", "published"].map(s => (
                <button key={s} type="button" onClick={() => setForm(f => ({...f, status: s}))} data-testid={`prod-status-${s}`} className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider-cap font-bold border-2 transition ${form.status === s ? (s === "published" ? "bg-[#22693C] text-white border-[#22693C]" : "bg-foreground/40 text-white border-foreground/40") : "bg-white text-foreground border-foreground/20 hover:border-foreground/40"}`}>{s === "published" ? "★ Published" : "Draft"}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2 sticky bottom-0 bg-white -mx-5 px-5 pb-2 border-t border-foreground/10">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 rounded-md font-bold uppercase tracking-wider-cap text-xs border-2 border-foreground/20 text-foreground hover:bg-foreground/5">Cancel</button>
            <button type="submit" disabled={saving} data-testid="prod-save-btn" className="flex-1 bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-4 py-3 rounded-md font-bold uppercase tracking-wider-cap text-xs disabled:opacity-50">{saving ? "Saving…" : (product ? "Save Changes" : "★ Add Product")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
